// Iter116.7 — App-level React error boundary.
//
// Before today, a runtime error in any page component (e.g. the Leaderboard
// `Zap is not defined` crash) would leave the operator staring at a blank
// screen with no recovery path. This boundary catches any render-time
// exception and shows a friendly recovery card with the error message + two
// recovery actions (try again / hard reload).
//
// Auto-resets when `resetKey` changes (we wire that to the current
// pathname in App.js so navigating away from a crashed page clears the
// boundary).

import React from "react";
import { AlertTriangle, RefreshCw, RotateCcw } from "lucide-react";

class PageErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Log to console so it still surfaces in DevTools / error tracking.
    // We never silently swallow: the operator's recovery card lives in render(),
    // but the underlying stack stays inspectable.
    console.error("[PageErrorBoundary] caught", error, info);
    this.setState({ info });
  }

  componentDidUpdate(prevProps) {
    if (prevProps.resetKey !== this.props.resetKey && this.state.hasError) {
      this.setState({ hasError: false, error: null, info: null });
    }
  }

  handleTryAgain = () => {
    this.setState({ hasError: false, error: null, info: null });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const message = this.state.error?.message || String(this.state.error || "Unknown error");
    return (
      <div data-testid="page-error-boundary" className="animate-fade-in flex items-center justify-center min-h-[60vh] p-6">
        <div className="max-w-lg w-full bg-[#121216] border border-[#FF3366]/30 rounded-sm p-6">
          <div className="flex items-center gap-3 mb-3">
            <AlertTriangle size={20} className="text-[#FF3366]" />
            <h2 className="text-lg font-black tracking-tight text-white">Something went wrong here</h2>
          </div>
          <p className="text-[12px] text-[#A0A0A8] leading-relaxed mb-4">
            This page hit a runtime error and stopped rendering. Your data is safe — nothing was
            written. Use the buttons below to recover, or hop to another page from the sidebar.
          </p>
          <div className="bg-[#08080A] border border-[#26262B] rounded-sm px-3 py-2 mb-4">
            <div className="text-[9px] font-bold uppercase tracking-[0.18em] text-[#5F5F66] mb-1">Error</div>
            <code data-testid="page-error-boundary-message"
                  className="text-[11px] font-mono text-[#FF3366] break-words">{message}</code>
          </div>
          <details className="mb-4 text-[10px] font-mono text-[#5F5F66]">
            <summary className="cursor-pointer hover:text-[#8A8A93] uppercase tracking-[0.15em]">
              Stack (for developers)
            </summary>
            <pre className="mt-2 p-2 bg-[#08080A] border border-[#26262B] rounded-sm overflow-x-auto whitespace-pre-wrap text-[#5F5F66]">
{this.state.info?.componentStack || this.state.error?.stack || "(no stack available)"}
            </pre>
          </details>
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid="page-error-boundary-try-again"
              onClick={this.handleTryAgain}
              className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.15em] px-3 py-2 rounded-sm border bg-[#00FF88]/10 text-[#00FF88] border-[#00FF88]/40 hover:bg-[#00FF88]/20"
            >
              <RotateCcw size={12} /> Try again
            </button>
            <button
              type="button"
              data-testid="page-error-boundary-reload"
              onClick={this.handleReload}
              className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.15em] px-3 py-2 rounded-sm border bg-[#0A0A0E] text-[#8A8A93] border-[#26262B] hover:border-[#FFB800]/40 hover:text-[#FFB800]"
            >
              <RefreshCw size={12} /> Reload page
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default PageErrorBoundary;
